// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useState } from 'react';

import { getApiBaseUrl } from '@/services/apiBaseUrl';
import type { RecentCourse } from '@/features/round-init/types';

type RecentCourseResponse = {
  courseId?: string;
  CourseId?: string;
  courseName?: string;
  CourseName?: string;
  courseAddress?: string;
  CourseAddress?: string;
  holesCount?: number;
  HolesCount?: number;
  playDate?: string | null;
  PlayDate?: string | null;
  lastPlayedAt?: string;
  LastPlayedAt?: string;
  lastRoundId?: string;
  LastRoundId?: string;
};

const mapRecentCourse = (row: RecentCourseResponse): RecentCourse | null => {
  const id = (row.courseId ?? row.CourseId ?? '').trim();
  const name = (row.courseName ?? row.CourseName ?? '').trim();
  if (!id || !name) {
    return null;
  }
  return {
    id,
    name,
    address: (row.courseAddress ?? row.CourseAddress ?? '').trim(),
    holesCount: row.holesCount ?? row.HolesCount ?? 18,
    playDate: row.playDate ?? row.PlayDate ?? null,
    lastPlayedAt: (row.lastPlayedAt ?? row.LastPlayedAt ?? '').trim(),
    lastRoundId: (row.lastRoundId ?? row.LastRoundId ?? '').trim(),
  };
};

/**
 * Resolves the player id for the signed-in user via /api/player/by-user/{userId}.
 * Returns null until it is known. Useful where the screen doesn't already track it.
 */
export function useCurrentPlayerId(userId: string | null | undefined, token: string | null | undefined): string | null {
  const [playerId, setPlayerId] = useState<string | null>(null);

  useEffect(() => {
    if (!userId || !token) {
      setPlayerId(null);
      return;
    }

    let cancelled = false;
    const apiBaseUrl = getApiBaseUrl();

    const resolvePlayerId = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/player/by-user/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok || cancelled) {
          return;
        }
        const payload = (await response.json()) as { id?: string };
        const id = payload.id?.trim();
        if (id && !cancelled) {
          setPlayerId(id);
        }
      } catch {
        // Convenience lookup; ignore failures.
      }
    };

    void resolvePlayerId();
    return () => {
      cancelled = true;
    };
  }, [userId, token]);

  return playerId;
}

/**
 * Short, localized "when last played" label for a recent course, e.g. "28 Apr".
 * Prefers the round's play date, falling back to when the round was created.
 */
export function formatLastPlayed(course: RecentCourse): string {
  const source = course.playDate || course.lastPlayedAt;
  if (!source) {
    return '';
  }
  const parsed = new Date(source);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  return parsed.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
}

/**
 * Loads the courses a player has most recently played (newest first, distinct
 * by course), used to surface a "Recently played" shortcut in the course picker.
 * Returns an empty list until a valid playerId and token are available.
 */
export function useRecentCourses(playerId: string | null | undefined, token: string | null | undefined, limit = 5): RecentCourse[] {
  const [recentCourses, setRecentCourses] = useState<RecentCourse[]>([]);

  useEffect(() => {
    if (!playerId || !token) {
      setRecentCourses([]);
      return;
    }

    let cancelled = false;
    const apiBaseUrl = getApiBaseUrl();

    const loadRecentCourses = async () => {
      try {
        const response = await fetch(
          `${apiBaseUrl}/api/rounds/players/${playerId}/recent-courses?limit=${limit}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok || cancelled) {
          return;
        }

        const payload = (await response.json()) as RecentCourseResponse[];
        if (cancelled || !Array.isArray(payload)) {
          return;
        }

        setRecentCourses(payload.map(mapRecentCourse).filter((course): course is RecentCourse => course !== null));
      } catch {
        // Recently-played is a convenience shortcut; ignore failures silently.
      }
    };

    void loadRecentCourses();
    return () => {
      cancelled = true;
    };
  }, [playerId, token, limit]);

  return recentCourses;
}
